import React, { useState, useCallback } from "react";
import { MdFitnessCenter } from "react-icons/md";
import { Exercise } from "../../types/exercise";
import { PaginatedResult } from "../../types/api";
import { ExerciseService } from "../../services/exerciseService";
import { useExerciseSearch } from "../../hooks/useExercises";
import { useAssignmentLists } from "../../hooks/useAssignmentLists";
import { useAssignmentModalState } from "../../hooks/useAssignmentModalState";
import { pageFrom } from "../../hooks/usePaginatedResource";
import {
  getTrainingMethodLabel,
  getTrainingMethodColor,
} from "../../utils/exerciseUtils";
import AssignmentModalLayout from "../modals/AssignmentModalLayout";
import AssignmentSearch from "../modals/AssignmentSearch";
import AssignmentPanel from "../modals/AssignmentPanel";
import AssignmentItemCard from "../modals/AssignmentItemCard";
import PersistentActionConfirmationModal from "../modals/PersistentActionConfirmationModal";

interface ExerciseWithActivityLog extends Exercise {
  activityLogId?: number;
}

interface PendingAction {
  type: "assign" | "unassign";
  exercise: ExerciseWithActivityLog;
}

interface ExerciseToTrainingDayModalProps {
  isOpen: boolean;
  onClose: () => void;
  trainingDayId: number;
  trainingDayName: string;
  currentExercises: ExerciseWithActivityLog[];
  onUpdate: () => void;
  userId?: number;
}

const createExerciseTags = (exercise: Exercise) => {
  if (!exercise.training_method) return [];

  return [
    {
      label: getTrainingMethodLabel(exercise.training_method),
      color: getTrainingMethodColor(exercise.training_method),
    },
  ];
};

const ExerciseToTrainingDayModal: React.FC<ExerciseToTrainingDayModalProps> = ({
  isOpen,
  onClose,
  trainingDayId,
  trainingDayName,
  currentExercises,
  onUpdate,
  userId,
}) => {
  // Every assign and unassign is confirmed first, with a "persistent" choice
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(
    null
  );

  const search = useExerciseSearch();

  // The assigned side comes from the training day already loaded by the page
  const loadAssigned = useCallback(
    async () => currentExercises,
    [currentExercises]
  );

  const loadAvailablePage = useCallback(
    async (page: number): Promise<PaginatedResult<Exercise> | null> => {
      const response = await ExerciseService.getExercises(page);
      if (!response) return null;
      return pageFrom(response.exercises, response);
    },
    []
  );

  const lists = useAssignmentLists<ExerciseWithActivityLog>({
    isOpen,
    loadAssigned,
    loadAvailablePage,
    search,
    errorMessage: "Failed to load exercises. Please try again.",
  });

  const modalState = useAssignmentModalState({
    isOpen,
    onClose,
    onUpdate,
    search,
    loadError: lists.error,
  });

  const requestUnassign = (exercise: ExerciseWithActivityLog) => {
    if (!exercise.activityLogId) {
      modalState.setError("Cannot unassign: Activity log ID not found");
      return;
    }
    setPendingAction({ type: "unassign", exercise });
  };

  const assign = async (exercise: Exercise, isPersistent: boolean) => {
    await modalState.runAction({
      kind: "assign",
      id: exercise.id,
      request: () =>
        ExerciseService.assignExerciseToTrainingDay(
          exercise.id,
          trainingDayId,
          isPersistent,
          userId
        ),
      onSuccess: (result) => {
        const activityLogId =
          (result.data as any)?.id || (result.data as any)?.data?.id;
        lists.markAssigned({ ...exercise, activityLogId });
      },
      successMessage: `${exercise.name} assigned successfully${
        isPersistent ? " to all matching training days" : ""
      }`,
      failureMessage: "Failed to assign exercise",
    });
    setPendingAction(null);
  };

  const unassign = async (
    exercise: ExerciseWithActivityLog,
    isPersistent: boolean
  ) => {
    await modalState.runAction({
      kind: "unassign",
      id: exercise.id,
      request: () =>
        ExerciseService.unassignExerciseFromTrainingDay(
          exercise.activityLogId,
          isPersistent
        ),
      onSuccess: () => lists.markUnassigned(exercise),
      successMessage: `${exercise.name} unassigned successfully${
        isPersistent ? " from all matching training days" : ""
      }`,
      failureMessage: "Failed to unassign exercise",
    });
    setPendingAction(null);
  };

  const handleConfirmAction = (isPersistent: boolean) => {
    if (!pendingAction) return;

    if (pendingAction.type === "assign") {
      assign(pendingAction.exercise, isPersistent);
    } else {
      unassign(pendingAction.exercise, isPersistent);
    }
  };

  const isPendingActionLoading = pendingAction
    ? pendingAction.type === "assign"
      ? modalState.assigningItems.has(pendingAction.exercise.id)
      : modalState.unassigningItems.has(pendingAction.exercise.id)
    : false;

  return (
    <AssignmentModalLayout
      isOpen={isOpen}
      onClose={modalState.handleClose}
      title="Manage Exercises"
      subtitle={trainingDayName}
      message={modalState.message}
    >
      <AssignmentSearch
        searchTerm={modalState.searchTerm}
        onSearchChange={modalState.setSearchTerm}
        onSearch={modalState.handleSearch}
        onClearSearch={modalState.handleClearSearch}
        placeholder="Search exercises by name..."
        hasSearched={search.hasSearched}
        searchLoading={search.loading}
      />

      <div className="flex flex-col overflow-hidden md:flex-row">
        <AssignmentPanel
          title="Available Exercises"
          countLabel={
            search.hasSearched
              ? `${lists.availableTotal} exercises found`
              : `${lists.availableTotal} exercises`
          }
          tone="available"
          loading={lists.availableLoading}
          isEmpty={lists.available.length === 0}
          emptyIcon={MdFitnessCenter}
          emptyTitle={
            modalState.searchTerm
              ? "No exercises match your search"
              : "No exercises available"
          }
          onScroll={lists.handleAvailableScroll}
          loadingMore={lists.loadingMore}
          loadingMoreLabel="Loading more exercises..."
        >
          {lists.available.map((exercise) => (
            <AssignmentItemCard
              key={`available-${exercise.id}`}
              id={exercise.id}
              title={exercise.name}
              tags={createExerciseTags(exercise)}
              isAssigned={false}
              onToggle={() => setPendingAction({ type: "assign", exercise })}
              isToggling={modalState.assigningItems.has(exercise.id)}
              showImage={false}
            />
          ))}
        </AssignmentPanel>

        <AssignmentPanel
          title="Assigned To Training Day"
          countLabel={`${lists.assigned.length} exercises`}
          tone="assigned"
          loading={lists.assignedLoading}
          isEmpty={lists.assigned.length === 0}
          emptyIcon={MdFitnessCenter}
          emptyTitle="No exercises assigned"
        >
          {lists.assigned.map((exercise) => (
            <AssignmentItemCard
              key={`assigned-${exercise.id}`}
              id={exercise.id}
              title={exercise.name}
              tags={createExerciseTags(exercise)}
              isAssigned={true}
              onToggle={() => requestUnassign(exercise)}
              isToggling={modalState.unassigningItems.has(exercise.id)}
              onClick={(id) =>
                window.open(`/admin/exercise-library/${id}`, "_blank")
              }
              showImage={false}
            />
          ))}
        </AssignmentPanel>
      </div>

      <PersistentActionConfirmationModal
        isOpen={pendingAction !== null}
        onClose={() => setPendingAction(null)}
        onConfirm={handleConfirmAction}
        actionType={pendingAction?.type || "assign"}
        exerciseName={pendingAction?.exercise.name || ""}
        loading={isPendingActionLoading}
      />
    </AssignmentModalLayout>
  );
};

export default ExerciseToTrainingDayModal;
